import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { getConfig } from '../runtimeConfig';

const API = getConfig().REACT_APP_API;

// ===== TYPES =====
interface Filters {
  warehouses: string[];
  shelfSizes: string[];
  storageAreas: string[];
  levels: string[];
}

interface WarehouseSummary {
  WAREHOUSE: string;
  TOTAL_BINS: number;
  TOTAL_SEGMENTS: number;
  FREE_SEGMENTS: number;
  USED_SEGMENTS: number;
  UTILIZATION_PCT: number;
  AREAS: AreaSummary[];
}

interface AreaSummary {
  STORAGE_AREA: string;
  TOTAL_BINS: number;
  TOTAL_SEGMENTS: number;
  FREE_SEGMENTS: number;
  USED_SEGMENTS: number;
  UTILIZATION_PCT: number;
  TEMP_BLOCKED_BINS: number;
  TEMP_BLOCKED_PALLETS: number;
  AVG_LEVELS: number;
  SHELF_SIZES: ShelfSummary[];
}

interface ShelfSummary {
  SHELF_SIZE: string;
  TOTAL_BINS: number;
  TOTAL_SEGMENTS: number;
  FREE_SEGMENTS: number;
  USED_SEGMENTS: number;
  UTILIZATION_PCT: number;
}

// ===== STYLES =====
const COLORS = {
  primary: '#6366f1',
  primaryLight: '#eef2ff',
  success: '#10b981',
  successLight: '#ecfdf5',
  warning: '#f59e0b',
  warningLight: '#fffbeb',
  danger: '#ef4444',
  dangerLight: '#fef2f2',
  text: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  bg: '#f8fafc',
  card: '#ffffff',
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: COLORS.text,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: COLORS.textMuted,
    marginTop: '4px',
  },
  printBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: COLORS.text,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    padding: '20px',
    background: COLORS.card,
    borderRadius: '14px',
    border: `1px solid ${COLORS.border}`,
    marginBottom: '20px',
    flexWrap: 'wrap' as const,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    flex: '1 1 200px',
    minWidth: '180px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: COLORS.textMuted,
  },
  filterRequired: {
    color: COLORS.danger,
    marginLeft: '2px',
  },
  select: {
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1.5px solid ${COLORS.border}`,
    fontSize: '13px',
    color: COLORS.text,
    background: '#fff',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '32px',
  },
  summaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  summaryCard: {
    padding: '16px',
    background: COLORS.card,
    borderRadius: '12px',
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    textAlign: 'center' as const,
  },
  summaryLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: COLORS.textMuted,
    marginBottom: '6px',
  },
  summaryValue: {
    fontSize: '22px',
    fontWeight: 800,
    color: COLORS.text,
    fontFamily: "'JetBrains Mono', monospace",
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: COLORS.card,
    borderRadius: '14px',
    overflow: 'hidden',
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  th: {
    padding: '14px 16px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: COLORS.textMuted,
    background: '#f8fafc',
    borderBottom: `2px solid ${COLORS.border}`,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
  thRight: {
    padding: '14px 16px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: COLORS.textMuted,
    background: '#f8fafc',
    borderBottom: `2px solid ${COLORS.border}`,
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '12px 16px',
    fontSize: '13px',
    color: COLORS.text,
    borderBottom: `1px solid #f1f5f9`,
  },
  tdRight: {
    padding: '12px 16px',
    fontSize: '13px',
    color: COLORS.text,
    borderBottom: `1px solid #f1f5f9`,
    textAlign: 'right' as const,
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: 500,
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '16px',
    transition: 'all 0.2s',
    color: COLORS.primary,
  },
  drillRow: {
    background: '#fafbff',
  },
  drillTd: {
    padding: '0 16px 16px 48px',
    borderBottom: `1px solid #f1f5f9`,
  },
  utilBar: {
    height: '8px',
    borderRadius: '4px',
    background: '#f1f5f9',
    overflow: 'hidden',
    width: '100px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  noData: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: COLORS.textMuted,
    fontSize: '15px',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.3px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: COLORS.textSecondary,
    fontSize: '14px',
  },
};

// ===== UTILITY COMPONENTS =====
const UtilizationBar = ({ pct }: { pct: number }) => {
  const color = pct >= 90 ? COLORS.danger : pct >= 75 ? COLORS.warning : COLORS.success;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: '13px', color }}>{pct.toFixed(1)}%</span>
      <div style={styles.utilBar}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: '4px', transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
};

const StatusBadge = ({ pct }: { pct: number }) => {
  const config = pct >= 90
    ? { label: 'CRITICAL', bg: COLORS.dangerLight, color: COLORS.danger }
    : pct >= 75
    ? { label: 'HIGH', bg: COLORS.warningLight, color: COLORS.warning }
    : pct >= 50
    ? { label: 'MODERATE', bg: '#f0f9ff', color: '#0284c7' }
    : { label: 'HEALTHY', bg: COLORS.successLight, color: COLORS.success };
  return <span style={{ ...styles.badge, background: config.bg, color: config.color }}>{config.label}</span>;
};

const NumCell = ({ value }: { value: number }) => (
  <td style={styles.tdRight}>{value.toLocaleString()}</td>
);

// ===== WAREHOUSE NAME MAP =====
const WH_NAMES: Record<string, string> = {
  'DPW': 'Dammam Port WH', 'DSI': 'Dammam 2nd', 'JDA': 'Jeddah Modon3A',
  'JDB': 'Jeddah 3B', 'JUB': 'Jubail WH', 'JUB_1': 'JUBAIL 1 WH',
  'JUB_2': 'JUBAIL 2', 'RUH': 'Hala Riyadh WH', 'RUH_M': 'HALA Riyadh WH',
};

// ===== MAIN COMPONENT =====
const OperationsConsole: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<Filters>({ warehouses: [], shelfSizes: [], storageAreas: [], levels: [] });
  const [rawData, setRawData] = useState<any[]>([]);
  const [virtualData, setVirtualData] = useState<any[]>([]);
  const [customerData, setCustomerData] = useState<any[]>([]);

  // Selected filters
  const [selWarehouse, setSelWarehouse] = useState('');
  const [selArea, setSelArea] = useState('');
  const [selShelfSize, setSelShelfSize] = useState('');

  // Drill-down state
  const [expandedWH, setExpandedWH] = useState<string | null>(null);
  const [expandedArea, setExpandedArea] = useState<string | null>(null);

  // ===== LOAD DATA =====
  const testConnection = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/test`);
      setConnected(res.data.success);
      if (res.data.success) {
        const filtersRes = await axios.get(`${API}/warehouse/filters`);
        if (filtersRes.data.success) setFilters(filtersRes.data.data);
      }
    } catch (err: any) {
      setConnected(false);
      setError(err.message);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [warehouseRes, virtualRes, customerRes] = await Promise.all([
        axios.get(`${API}/warehouse`, { timeout: 600000 }).catch(() => null),
        axios.get(`${API}/warehouse/virtual`, { timeout: 600000 }).catch(() => null),
        axios.get(`${API}/warehouse/customer-storage-merged`, { timeout: 600000 }).catch(() => null),
      ]);
      if (warehouseRes?.data?.success) setRawData(warehouseRes.data.data || []);
      if (virtualRes?.data?.success) setVirtualData(virtualRes.data.data || []);
      if (customerRes?.data?.success) setCustomerData(customerRes.data.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { testConnection(); }, [testConnection]);
  useEffect(() => { if (connected) loadData(); }, [connected, loadData]);

  // ===== AVAILABLE FILTER OPTIONS (cascading) =====
  const availableAreas = useMemo(() => {
    if (!selWarehouse || rawData.length === 0) return filters.storageAreas || [];
    return Array.from(new Set(rawData.filter(d => d.WAREHOUSE === selWarehouse).map(d => d.STORAGE_AREA).filter(Boolean))).sort();
  }, [selWarehouse, rawData, filters.storageAreas]);

  const availableShelfSizes = useMemo(() => {
    if (rawData.length === 0) return filters.shelfSizes || [];
    let filtered = rawData;
    if (selWarehouse) filtered = filtered.filter(d => d.WAREHOUSE === selWarehouse);
    if (selArea) filtered = filtered.filter(d => d.STORAGE_AREA === selArea);
    return Array.from(new Set(filtered.map(d => d.SHELF_SIZE).filter(Boolean))).sort();
  }, [selWarehouse, selArea, rawData, filters.shelfSizes]);

  // ===== FILTERED DATA =====
  const filteredData = useMemo(() => {
    let data = [...rawData];
    if (selWarehouse) data = data.filter(d => d.WAREHOUSE === selWarehouse);
    if (selArea) data = data.filter(d => d.STORAGE_AREA === selArea);
    if (selShelfSize) data = data.filter(d => d.SHELF_SIZE === selShelfSize);
    return data;
  }, [rawData, selWarehouse, selArea, selShelfSize]);

  const filteredVirtual = useMemo(() => {
    let data = [...virtualData];
    if (selWarehouse) data = data.filter(d => d.WAREHOUSE === selWarehouse);
    if (selArea) data = data.filter(d => d.STORAGE_AREA === selArea);
    return data;
  }, [virtualData, selWarehouse, selArea]);

  // ===== COMPUTE SUMMARIES =====
  const { totalStats, warehouseSummaries } = useMemo(() => {
    if (filteredData.length === 0) return { totalStats: null, warehouseSummaries: [] };

    const totalSegments = filteredData.reduce((s, d) => s + (d.TOTAL_NO_OF_SEGMENTS || 0), 0);
    const freeSegments = filteredData.reduce((s, d) => s + (d.FREE_SEGMENTS || 0), 0);
    const usedSegments = totalSegments - freeSegments;
    const utilPct = totalSegments > 0 ? (usedSegments / totalSegments) * 100 : 0;

    // Virtual pallets
    const vUsed = filteredVirtual.reduce((s, d) => s + ((d.TOTAL_NO_OF_SEGMENTS || 0) - (d.FREE_SEGMENTS || 0)), 0);

    // Floor pallets
    let filteredCustomer = customerData;
    if (selWarehouse) filteredCustomer = filteredCustomer.filter((d: any) => d.WAREHOUSE === selWarehouse);
    if (selArea) filteredCustomer = filteredCustomer.filter((d: any) => d.STORAGE_LOCATION && d.STORAGE_LOCATION.includes(selArea));
    const floorPallets = filteredCustomer.filter((d: any) => d.STORAGE_TYPE === 'FLOOR' || d.STORAGE_TYPE === 'BULK').reduce((s: number, d: any) => s + (d.USED_PALLETS || 0), 0);
    const manualPallets = filteredCustomer.filter((d: any) => d.STORAGE_TYPE === 'MANUAL').reduce((s: number, d: any) => s + (d.USED_PALLETS || 0), 0);

    // Temp blocked
    const tempBlocked = filteredData.filter(d => String(d.LPFSTA || '').trim() === '2' || d.OCCUPANCY_STATUS === 'TEMP_BLOCKED');
    const tempBlockedPallets = tempBlocked.reduce((s, d) => s + (d.TOTAL_NO_OF_SEGMENTS || 0), 0);

    const totalUsedAll = usedSegments + vUsed + floorPallets + manualPallets;
    const uniqueCustomers = new Set(filteredCustomer.map((d: any) => d.CUSTOMER)).size;

    // Build warehouse summaries
    const whMap: Record<string, any> = {};
    filteredData.forEach(d => {
      const wh = d.WAREHOUSE;
      if (!whMap[wh]) whMap[wh] = { WAREHOUSE: wh, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0, USED_SEGMENTS: 0, items: [] };
      whMap[wh].TOTAL_BINS++;
      whMap[wh].TOTAL_SEGMENTS += d.TOTAL_NO_OF_SEGMENTS || 0;
      whMap[wh].FREE_SEGMENTS += d.FREE_SEGMENTS || 0;
      whMap[wh].USED_SEGMENTS += (d.TOTAL_NO_OF_SEGMENTS || 0) - (d.FREE_SEGMENTS || 0);
      whMap[wh].items.push(d);
    });

    const warehouseSummaries: WarehouseSummary[] = Object.values(whMap).map((wh: any) => {
      const utilPct = wh.TOTAL_SEGMENTS > 0 ? (wh.USED_SEGMENTS / wh.TOTAL_SEGMENTS) * 100 : 0;

      // Build area summaries within this warehouse
      const areaMap: Record<string, any> = {};
      wh.items.forEach((d: any) => {
        const area = d.STORAGE_AREA || 'Unknown';
        if (!areaMap[area]) areaMap[area] = { STORAGE_AREA: area, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0, USED_SEGMENTS: 0, TEMP_BLOCKED_BINS: 0, TEMP_BLOCKED_PALLETS: 0, LEVELS: [], items: [] };
        areaMap[area].TOTAL_BINS++;
        areaMap[area].TOTAL_SEGMENTS += d.TOTAL_NO_OF_SEGMENTS || 0;
        areaMap[area].FREE_SEGMENTS += d.FREE_SEGMENTS || 0;
        areaMap[area].USED_SEGMENTS += (d.TOTAL_NO_OF_SEGMENTS || 0) - (d.FREE_SEGMENTS || 0);
        if (d.NUMBER_OF_LEVELS) areaMap[area].LEVELS.push(d.NUMBER_OF_LEVELS);
        if (String(d.LPFSTA || '').trim() === '2' || d.OCCUPANCY_STATUS === 'TEMP_BLOCKED') {
          areaMap[area].TEMP_BLOCKED_BINS++;
          areaMap[area].TEMP_BLOCKED_PALLETS += d.TOTAL_NO_OF_SEGMENTS || 0;
        }
        areaMap[area].items.push(d);
      });

      const areas: AreaSummary[] = Object.values(areaMap).map((a: any) => {
        // Shelf size breakdown within area
        const shelfMap: Record<string, any> = {};
        a.items.forEach((d: any) => {
          const shelf = d.SHELF_SIZE || 'Unknown';
          if (!shelfMap[shelf]) shelfMap[shelf] = { SHELF_SIZE: shelf, TOTAL_BINS: 0, TOTAL_SEGMENTS: 0, FREE_SEGMENTS: 0, USED_SEGMENTS: 0 };
          shelfMap[shelf].TOTAL_BINS++;
          shelfMap[shelf].TOTAL_SEGMENTS += d.TOTAL_NO_OF_SEGMENTS || 0;
          shelfMap[shelf].FREE_SEGMENTS += d.FREE_SEGMENTS || 0;
          shelfMap[shelf].USED_SEGMENTS += (d.TOTAL_NO_OF_SEGMENTS || 0) - (d.FREE_SEGMENTS || 0);
        });

        return {
          STORAGE_AREA: a.STORAGE_AREA,
          TOTAL_BINS: a.TOTAL_BINS,
          TOTAL_SEGMENTS: a.TOTAL_SEGMENTS,
          FREE_SEGMENTS: a.FREE_SEGMENTS,
          USED_SEGMENTS: a.USED_SEGMENTS,
          UTILIZATION_PCT: a.TOTAL_SEGMENTS > 0 ? (a.USED_SEGMENTS / a.TOTAL_SEGMENTS) * 100 : 0,
          TEMP_BLOCKED_BINS: a.TEMP_BLOCKED_BINS,
          TEMP_BLOCKED_PALLETS: a.TEMP_BLOCKED_PALLETS,
          AVG_LEVELS: a.LEVELS.length > 0 ? a.LEVELS.reduce((x: number, y: number) => x + y, 0) / a.LEVELS.length : 0,
          SHELF_SIZES: Object.values(shelfMap).map((s: any) => ({
            ...s,
            UTILIZATION_PCT: s.TOTAL_SEGMENTS > 0 ? (s.USED_SEGMENTS / s.TOTAL_SEGMENTS) * 100 : 0,
          })).sort((a: any, b: any) => b.TOTAL_BINS - a.TOTAL_BINS) as ShelfSummary[],
        };
      }).sort((a, b) => b.UTILIZATION_PCT - a.UTILIZATION_PCT);

      return { WAREHOUSE: wh.WAREHOUSE, TOTAL_BINS: wh.TOTAL_BINS, TOTAL_SEGMENTS: wh.TOTAL_SEGMENTS, FREE_SEGMENTS: wh.FREE_SEGMENTS, USED_SEGMENTS: wh.USED_SEGMENTS, UTILIZATION_PCT: utilPct, AREAS: areas };
    }).sort((a, b) => b.TOTAL_SEGMENTS - a.TOTAL_SEGMENTS);

    return {
      totalStats: {
        totalBins: filteredData.length,
        totalCapacity: totalSegments,
        usedPallets: usedSegments,
        freePallets: freeSegments,
        utilPct: Math.round(utilPct * 100) / 100,
        virtualPallets: vUsed,
        floorPallets,
        manualPallets,
        tempBlockedPallets,
        totalUsedAll: Math.round(totalUsedAll),
        customers: uniqueCustomers,
        warehouses: Object.keys(whMap).length,
      },
      warehouseSummaries,
    };
  }, [filteredData, filteredVirtual, customerData, selWarehouse, selArea]);

  // ===== HANDLERS =====
  const handlePrint = () => window.print();

  const toggleWH = (wh: string) => {
    setExpandedWH(prev => prev === wh ? null : wh);
    setExpandedArea(null);
  };

  const toggleArea = (area: string) => {
    setExpandedArea(prev => prev === area ? null : area);
  };

  // Reset dependent filters on warehouse change
  useEffect(() => { if (!selWarehouse) { setSelArea(''); setSelShelfSize(''); } }, [selWarehouse]);
  useEffect(() => { if (!selArea) setSelShelfSize(''); }, [selArea]);

  // ===== RENDER =====
  const filtersSelected = selWarehouse !== '';
  const now = new Date();
  const timeStr = now.toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={styles.container} className="operations-console">
      {/* HEADER */}
      <div style={styles.header} className="no-print-margin">
        <div>
          <h1 style={styles.title}>Operations Console</h1>
          <div style={styles.subtitle}>{timeStr} • {rawData.length > 0 ? `${rawData.length.toLocaleString()} locations loaded` : 'Loading...'}</div>
        </div>
        <button style={styles.printBtn} onClick={handlePrint} className="no-print">
          🖨️ Print Report
        </button>
      </div>

      {/* FILTER BAR */}
      <div style={styles.filterBar} className="no-print-break">
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>
            Warehouse <span style={styles.filterRequired}>*</span>
          </label>
          <select
            style={{ ...styles.select, borderColor: !selWarehouse ? '#fca5a5' : COLORS.border }}
            value={selWarehouse}
            onChange={e => { setSelWarehouse(e.target.value); setSelArea(''); setSelShelfSize(''); setExpandedWH(null); setExpandedArea(null); }}
          >
            <option value="">— Select Warehouse —</option>
            {(filters.warehouses || []).map(wh => (
              <option key={wh} value={wh}>{wh} — {WH_NAMES[wh] || wh}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Storage Area</label>
          <select style={styles.select} value={selArea} onChange={e => { setSelArea(e.target.value); setSelShelfSize(''); setExpandedArea(null); }} disabled={!selWarehouse}>
            <option value="">All Areas</option>
            {availableAreas.map((a: any) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Pallet Size</label>
          <select style={styles.select} value={selShelfSize} onChange={e => setSelShelfSize(e.target.value)} disabled={!selWarehouse}>
            <option value="">All Sizes</option>
            {availableShelfSizes.map((s: any) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '8px', paddingBottom: '2px' }}>
          <button
            onClick={() => { setSelWarehouse(''); setSelArea(''); setSelShelfSize(''); setExpandedWH(null); setExpandedArea(null); }}
            style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, background: '#fff', fontSize: '13px', cursor: 'pointer', color: COLORS.textSecondary, fontWeight: 500 }}
            className="no-print"
          >
            Clear
          </button>
        </div>
      </div>

      {/* LOADING / ERROR / NO SELECTION */}
      {loading && <div style={styles.loading}>⏳ Loading warehouse data...</div>}
      {error && <div style={{ ...styles.noData, color: COLORS.danger }}>❌ {error}</div>}
      {!loading && connected === false && <div style={styles.noData}>Unable to connect to server</div>}

      {!loading && connected && !filtersSelected && (
        <div style={styles.noData}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📦</div>
          <div style={{ fontWeight: 600, color: COLORS.text, marginBottom: '4px' }}>Select a warehouse to begin</div>
          <div>Use the filters above to view warehouse operations data</div>
        </div>
      )}

      {/* SUMMARY STRIP */}
      {!loading && filtersSelected && totalStats && (
        <>
          <div style={styles.summaryStrip}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Total Capacity</div>
              <div style={styles.summaryValue}>{totalStats.totalCapacity.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '2px' }}>pallets</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Used Pallets</div>
              <div style={{ ...styles.summaryValue, color: COLORS.primary }}>{totalStats.usedPallets.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '2px' }}>bin storage</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Free Space</div>
              <div style={{ ...styles.summaryValue, color: COLORS.success }}>{totalStats.freePallets.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '2px' }}>available</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Utilization</div>
              <div style={{ ...styles.summaryValue, color: totalStats.utilPct >= 90 ? COLORS.danger : totalStats.utilPct >= 75 ? COLORS.warning : COLORS.success }}>{totalStats.utilPct.toFixed(1)}%</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Virtual Pallets</div>
              <div style={styles.summaryValue}>{totalStats.virtualPallets.toLocaleString()}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Floor / Manual</div>
              <div style={styles.summaryValue}>{(totalStats.floorPallets + totalStats.manualPallets).toLocaleString()}</div>
            </div>
            {totalStats.tempBlockedPallets > 0 && (
              <div style={{ ...styles.summaryCard, borderColor: '#fca5a5' }}>
                <div style={styles.summaryLabel}>Temp Blocked</div>
                <div style={{ ...styles.summaryValue, color: COLORS.danger }}>{totalStats.tempBlockedPallets.toLocaleString()}</div>
              </div>
            )}
          </div>

          {/* DATA TABLE WITH DRILL-DOWN */}
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '40px' }}></th>
                <th style={styles.th}>Warehouse</th>
                <th style={styles.thRight}>Bins</th>
                <th style={styles.thRight}>Capacity</th>
                <th style={styles.thRight}>Used</th>
                <th style={styles.thRight}>Free</th>
                <th style={styles.thRight}>Utilization</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {warehouseSummaries.map(wh => (
                <React.Fragment key={wh.WAREHOUSE}>
                  {/* Warehouse Row */}
                  <tr style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onClick={() => toggleWH(wh.WAREHOUSE)}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}>
                    <td style={styles.td}>
                      <span style={{ ...styles.expandBtn, transform: expandedWH === wh.WAREHOUSE ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.2s' }}>▶</span>
                    </td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>
                      <div>{wh.WAREHOUSE}</div>
                      <div style={{ fontSize: '11px', color: COLORS.textMuted, fontWeight: 400 }}>{WH_NAMES[wh.WAREHOUSE] || ''} • {wh.AREAS.length} areas</div>
                    </td>
                    <NumCell value={wh.TOTAL_BINS} />
                    <NumCell value={wh.TOTAL_SEGMENTS} />
                    <NumCell value={wh.USED_SEGMENTS} />
                    <NumCell value={wh.FREE_SEGMENTS} />
                    <td style={styles.tdRight}><UtilizationBar pct={wh.UTILIZATION_PCT} /></td>
                    <td style={{ ...styles.td, textAlign: 'center' }}><StatusBadge pct={wh.UTILIZATION_PCT} /></td>
                  </tr>

                  {/* Drill Level 1: Areas */}
                  {expandedWH === wh.WAREHOUSE && wh.AREAS.map(area => (
                    <React.Fragment key={area.STORAGE_AREA}>
                      <tr style={{ ...styles.drillRow, cursor: 'pointer' }}
                          onClick={() => toggleArea(area.STORAGE_AREA)}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f0f2ff')}
                          onMouseLeave={e => (e.currentTarget.style.background = '#fafbff')}>
                        <td style={{ ...styles.td, paddingLeft: '32px' }}>
                          <span style={{ ...styles.expandBtn, transform: expandedArea === area.STORAGE_AREA ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.2s', fontSize: '12px' }}>▶</span>
                        </td>
                        <td style={{ ...styles.td, fontWeight: 600, fontSize: '12px', paddingLeft: '8px' }}>
                          <span style={{ color: COLORS.primary }}>↳</span> {area.STORAGE_AREA}
                          {area.TEMP_BLOCKED_BINS > 0 && <span style={{ ...styles.badge, background: COLORS.dangerLight, color: COLORS.danger, marginLeft: '8px', fontSize: '9px' }}>{area.TEMP_BLOCKED_BINS} blocked</span>}
                          <span style={{ color: COLORS.textMuted, fontWeight: 400, marginLeft: '8px' }}>Avg {area.AVG_LEVELS.toFixed(1)} levels</span>
                        </td>
                        <NumCell value={area.TOTAL_BINS} />
                        <NumCell value={area.TOTAL_SEGMENTS} />
                        <NumCell value={area.USED_SEGMENTS} />
                        <NumCell value={area.FREE_SEGMENTS} />
                        <td style={styles.tdRight}><UtilizationBar pct={area.UTILIZATION_PCT} /></td>
                        <td style={{ ...styles.td, textAlign: 'center' }}><StatusBadge pct={area.UTILIZATION_PCT} /></td>
                      </tr>

                      {/* Drill Level 2: Shelf Sizes */}
                      {expandedArea === area.STORAGE_AREA && area.SHELF_SIZES.map(shelf => (
                        <tr key={shelf.SHELF_SIZE} style={{ background: '#f5f3ff' }}>
                          <td style={{ ...styles.td, paddingLeft: '60px' }}></td>
                          <td style={{ ...styles.td, fontSize: '12px', color: COLORS.textSecondary, paddingLeft: '16px' }}>
                            <span style={{ color: COLORS.textMuted }}>↳</span> Pallet Size: <strong>{shelf.SHELF_SIZE}</strong>
                          </td>
                          <NumCell value={shelf.TOTAL_BINS} />
                          <NumCell value={shelf.TOTAL_SEGMENTS} />
                          <NumCell value={shelf.USED_SEGMENTS} />
                          <NumCell value={shelf.FREE_SEGMENTS} />
                          <td style={styles.tdRight}><UtilizationBar pct={shelf.UTILIZATION_PCT} /></td>
                          <td style={{ ...styles.td, textAlign: 'center' }}><StatusBadge pct={shelf.UTILIZATION_PCT} /></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}

              {/* TOTALS ROW */}
              {warehouseSummaries.length > 1 && totalStats && (
                <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                  <td style={styles.td}></td>
                  <td style={{ ...styles.td, fontWeight: 800, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>TOTAL</td>
                  <td style={styles.tdRight}><strong>{totalStats.totalBins.toLocaleString()}</strong></td>
                  <td style={styles.tdRight}><strong>{totalStats.totalCapacity.toLocaleString()}</strong></td>
                  <td style={styles.tdRight}><strong>{totalStats.usedPallets.toLocaleString()}</strong></td>
                  <td style={styles.tdRight}><strong>{totalStats.freePallets.toLocaleString()}</strong></td>
                  <td style={styles.tdRight}><UtilizationBar pct={totalStats.utilPct} /></td>
                  <td style={{ ...styles.td, textAlign: 'center' }}><StatusBadge pct={totalStats.utilPct} /></td>
                </tr>
              )}
            </tbody>
          </table>

          {/* PRINT FOOTER */}
          <div className="print-only" style={{ marginTop: '20px', paddingTop: '12px', borderTop: `1px solid ${COLORS.border}`, fontSize: '10px', color: COLORS.textMuted, display: 'flex', justifyContent: 'space-between' }}>
            <span>HALA WMS — Operations Console</span>
            <span>Generated: {timeStr}</span>
          </div>
        </>
      )}

      {/* PRINT STYLES */}
      <style>{`
        @media print {
          body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-only { display: flex !important; }
          .operations-console { padding: 0 !important; max-width: 100% !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          @page { margin: 1cm; size: landscape; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>
    </div>
  );
};

export default OperationsConsole;
